import type { ActiveQuest, SkillDefinition, Buff } from 'shared';
import { QUESTS, SKILLS } from 'shared';

export interface HUDConfig {
  maxHealth: number;
  maxMana: number;
}

export class HUD {
  private container: HTMLDivElement;

  // Stats
  private health: number;
  private maxHealth: number;
  private mana: number;
  private maxMana: number;
  private level: number = 1;
  private experience: number = 0;
  private experienceToLevel: number = 100;
  private gold: number = 0;

  // Elements
  private healthBar: HTMLDivElement;
  private healthText: HTMLDivElement;
  private manaBar: HTMLDivElement;
  private manaText: HTMLDivElement;
  private levelText: HTMLDivElement;
  private expBar: HTMLDivElement;
  private goldText: HTMLDivElement;
  private questTracker: HTMLDivElement;
  private targetFrame: HTMLDivElement;
  private actionBar: HTMLDivElement;
  private actionSlots: HTMLDivElement[] = [];
  private castBar: HTMLDivElement;
  private buffContainer: HTMLDivElement;

  // Action bar state
  private currentActionBar: string[] = [];
  public onSkillClick?: (slotIndex: number) => void;

  // Notifications
  private notificationQueue: { message: string; color: string }[] = [];

  // PERF: Buff caching to avoid DOM rebuilds
  private buffElements: Map<string, HTMLDivElement> = new Map();
  private lastBuffIds: string = '';

  constructor(config: HUDConfig) {
    this.maxHealth = config.maxHealth;
    this.health = config.maxHealth;
    this.maxMana = config.maxMana;
    this.mana = config.maxMana;

    this.container = document.createElement('div');
    this.container.id = 'hud';
    this.container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 100;
      font-family: 'Segoe UI', Arial, sans-serif;
    `;

    // Create all HUD elements (these add to this.container)
    this.createPlayerFrame();
    this.questTracker = this.createQuestTracker();
    this.targetFrame = this.createTargetFrame();
    this.actionBar = this.createActionBar();
    this.castBar = this.createCastBar();
    this.buffContainer = this.createBuffContainer();

    // Create notification area
    this.createNotificationArea();

    // Append container to DOM first, then query for elements
    document.getElementById('game-container')?.appendChild(this.container);

    // Now we can safely query for elements by ID (they're in the DOM now)
    this.healthBar = document.getElementById('health-bar-fill') as HTMLDivElement;
    this.manaBar = document.getElementById('mana-bar-fill') as HTMLDivElement;
    this.healthText = document.getElementById('health-text') as HTMLDivElement;
    this.manaText = document.getElementById('mana-text') as HTMLDivElement;
    this.levelText = document.getElementById('level-text') as HTMLDivElement;
    this.expBar = document.getElementById('exp-bar-fill') as HTMLDivElement;
    this.goldText = document.getElementById('gold-text') as HTMLDivElement;

    // Initial update
    this.updateHealthBar();
    this.updateManaBar();
  }

  private createPlayerFrame(): void {
    const frame = document.createElement('div');
    frame.id = 'player-frame';
    frame.style.cssText = `
      position: absolute;
      top: 15px;
      left: 15px;
      width: 220px;
      background: rgba(0,0,0,0.7);
      border: 2px solid #4a4a6a;
      border-radius: 8px;
      padding: 10px;
      pointer-events: auto;
    `;

    frame.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <div id="level-text" style="
          background: linear-gradient(180deg, #ffd700, #b8860b);
          color: #000;
          font-weight: bold;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          margin-right: 10px;
        ">1</div>
        <div style="flex: 1;">
          <div style="color: #e0e0e0; font-size: 14px; font-weight: bold;">Hero</div>
        </div>
        <div id="gold-text" style="color: #ffd700; font-size: 13px;">💰 0</div>
      </div>

      <div style="margin-bottom: 6px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span style="color: #66ff66; font-size: 11px;">HP</span>
          <span id="health-text" style="color: #66ff66; font-size: 11px;">100/100</span>
        </div>
        <div style="background: #333; border-radius: 4px; height: 12px; overflow: hidden;">
          <div id="health-bar-fill" style="
            background: linear-gradient(180deg, #44ff44, #22aa22);
            height: 100%;
            width: 100%;
            transition: width 0.3s ease;
          "></div>
        </div>
      </div>

      <div style="margin-bottom: 6px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span style="color: #6699ff; font-size: 11px;">MP</span>
          <span id="mana-text" style="color: #6699ff; font-size: 11px;">100/100</span>
        </div>
        <div style="background: #333; border-radius: 4px; height: 10px; overflow: hidden;">
          <div id="mana-bar-fill" style="
            background: linear-gradient(180deg, #4488ff, #2266cc);
            height: 100%;
            width: 100%;
            transition: width 0.3s ease;
          "></div>
        </div>
      </div>

      <div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span style="color: #cc99ff; font-size: 10px;">EXP</span>
          <span id="exp-text" style="color: #cc99ff; font-size: 10px;">0/100</span>
        </div>
        <div style="background: #333; border-radius: 3px; height: 6px; overflow: hidden;">
          <div id="exp-bar-fill" style="
            background: linear-gradient(180deg, #9966ff, #6633cc);
            height: 100%;
            width: 0%;
            transition: width 0.3s ease;
          "></div>
        </div>
      </div>
    `;

    this.container.appendChild(frame);
  }

  private createQuestTracker(): HTMLDivElement {
    const tracker = document.createElement('div');
    tracker.id = 'quest-tracker';
    tracker.style.cssText = `
      position: absolute;
      top: 15px;
      right: 15px;
      width: 220px;
      background: rgba(0,0,0,0.6);
      border: 1px solid #4a4a6a;
      border-radius: 6px;
      padding: 10px;
      pointer-events: auto;
    `;

    tracker.innerHTML = `
      <div style="color: #ffd700; font-size: 13px; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #4a4a6a; padding-bottom: 5px;">
        📜 Quests
      </div>
      <div id="quest-list" style="font-size: 12px; color: #ccc;">
        <div style="color: #888; font-style: italic;">No active quests</div>
      </div>
    `;

    this.container.appendChild(tracker);
    return tracker;
  }

  private createTargetFrame(): HTMLDivElement {
    const frame = document.createElement('div');
    frame.id = 'target-frame';
    frame.style.cssText = `
      position: absolute;
      top: 15px;
      left: 50%;
      transform: translateX(-50%);
      width: 200px;
      background: rgba(0,0,0,0.7);
      border: 2px solid #8b0000;
      border-radius: 6px;
      padding: 8px;
      display: none;
    `;

    frame.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 5px;">
        <span id="target-level" style="
          background: #8b0000;
          color: #fff;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          margin-right: 8px;
        ">1</span>
        <span id="target-name" style="color: #ff6666; font-size: 13px; font-weight: bold;">Enemy</span>
      </div>
      <div style="background: #333; border-radius: 4px; height: 10px; overflow: hidden;">
        <div id="target-health-bar" style="
          background: linear-gradient(180deg, #ff4444, #aa2222);
          height: 100%;
          width: 100%;
          transition: width 0.2s ease;
        "></div>
      </div>
    `;

    this.container.appendChild(frame);
    return frame;
  }

  private createActionBar(): HTMLDivElement {
    const bar = document.createElement('div');
    bar.id = 'action-bar';
    bar.style.cssText = `
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 5px;
      background: rgba(0,0,0,0.7);
      padding: 10px;
      border-radius: 8px;
      border: 2px solid #4a4a6a;
      pointer-events: auto;
    `;

    // Create 6 skill slots
    for (let i = 0; i < 6; i++) {
      const slot = this.createActionSlot(i);
      this.actionSlots.push(slot);
      bar.appendChild(slot);
    }

    this.container.appendChild(bar);
    return bar;
  }

  private createActionSlot(index: number): HTMLDivElement {
    const slot = document.createElement('div');
    slot.className = 'action-slot';
    slot.dataset.index = index.toString();
    slot.style.cssText = `
      width: 50px;
      height: 50px;
      background: rgba(30,30,50,0.9);
      border: 2px solid #3a3a5a;
      border-radius: 6px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: border-color 0.2s, transform 0.1s;
      position: relative;
      overflow: hidden;
    `;

    // Icon
    const icon = document.createElement('span');
    icon.className = 'skill-icon';
    icon.style.cssText = `font-size: 24px; z-index: 2;`;
    slot.appendChild(icon);

    // Cooldown overlay (radial sweep like WoW)
    const cooldownOverlay = document.createElement('div');
    cooldownOverlay.className = 'cooldown-overlay';
    cooldownOverlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 3;
      pointer-events: none;
      opacity: 0;
    `;
    // Use conic-gradient for radial sweep effect
    cooldownOverlay.innerHTML = `
      <svg viewBox="0 0 50 50" style="width: 100%; height: 100%; transform: rotate(-90deg);">
        <circle class="cooldown-sweep" cx="25" cy="25" r="23"
          fill="none"
          stroke="rgba(0,0,0,0.7)"
          stroke-width="50"
          stroke-dasharray="144.51 144.51"
          stroke-dashoffset="0"
        />
      </svg>
    `;
    slot.appendChild(cooldownOverlay);

    // Cooldown bling/flash effect
    const cooldownBling = document.createElement('div');
    cooldownBling.className = 'cooldown-bling';
    cooldownBling.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%);
      opacity: 0;
      z-index: 5;
      pointer-events: none;
    `;
    slot.appendChild(cooldownBling);

    // Cooldown text
    const cooldownText = document.createElement('span');
    cooldownText.className = 'cooldown-text';
    cooldownText.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 16px;
      font-weight: bold;
      color: #fff;
      text-shadow: 1px 1px 2px #000;
      z-index: 4;
      display: none;
    `;
    slot.appendChild(cooldownText);

    // Keybind label
    const keyLabel = document.createElement('span');
    keyLabel.className = 'key-label';
    keyLabel.style.cssText = `
      position: absolute;
      bottom: 2px;
      right: 3px;
      font-size: 10px;
      color: #888;
      z-index: 2;
    `;
    keyLabel.textContent = (index + 1).toString();
    slot.appendChild(keyLabel);

    // Mana cost indicator
    const manaCost = document.createElement('span');
    manaCost.className = 'mana-cost';
    manaCost.style.cssText = `
      position: absolute;
      bottom: 2px;
      left: 3px;
      font-size: 9px;
      color: #6699ff;
      z-index: 2;
    `;
    slot.appendChild(manaCost);

    // Hover effects
    slot.addEventListener('mouseenter', () => {
      slot.style.borderColor = '#6a6a9a';
      slot.style.transform = 'scale(1.05)';
      this.showSkillTooltip(index);
    });
    slot.addEventListener('mouseleave', () => {
      slot.style.borderColor = '#3a3a5a';
      slot.style.transform = 'scale(1)';
      this.hideSkillTooltip();
    });

    // Click to use skill
    slot.addEventListener('click', () => {
      this.onSkillClick?.(index);
    });

    return slot;
  }

  private createCastBar(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'cast-bar-container';
    container.style.cssText = `
      position: absolute;
      bottom: 90px;
      left: 50%;
      transform: translateX(-50%);
      width: 300px;
      display: none;
    `;

    container.innerHTML = `
      <div id="cast-bar-name" style="
        color: #fff;
        font-size: 12px;
        text-align: center;
        margin-bottom: 3px;
        text-shadow: 1px 1px 2px #000;
      ">Casting...</div>
      <div style="
        background: rgba(0,0,0,0.7);
        border: 2px solid #4a4a6a;
        border-radius: 4px;
        height: 16px;
        overflow: hidden;
      ">
        <div id="cast-bar-fill" style="
          background: linear-gradient(180deg, #ff9900, #cc6600);
          height: 100%;
          width: 0%;
          transition: width 0.05s linear;
        "></div>
      </div>
      <div id="cast-bar-time" style="
        color: #ccc;
        font-size: 10px;
        text-align: center;
        margin-top: 2px;
      ">0.0s</div>
    `;

    this.container.appendChild(container);
    return container;
  }

  private createBuffContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'buff-container';
    container.style.cssText = `
      position: absolute;
      top: 180px;
      left: 15px;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      max-width: 200px;
    `;

    this.container.appendChild(container);
    return container;
  }

  private skillTooltip: HTMLDivElement | null = null;

  private showSkillTooltip(slotIndex: number): void {
    const skillId = this.currentActionBar[slotIndex];
    if (!skillId) return;

    const skill = SKILLS[skillId];
    if (!skill) return;

    if (!this.skillTooltip) {
      this.skillTooltip = document.createElement('div');
      this.skillTooltip.id = 'skill-tooltip';
      this.skillTooltip.style.cssText = `
        position: absolute;
        bottom: 85px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(20,20,30,0.95);
        border: 2px solid #5a5a8a;
        border-radius: 6px;
        padding: 10px;
        min-width: 200px;
        max-width: 280px;
        z-index: 200;
        pointer-events: none;
      `;
      this.container.appendChild(this.skillTooltip);
    }

    const damageTypes: Record<string, string> = {
      physical: '#fff',
      fire: '#ff6600',
      ice: '#00ccff',
      lightning: '#ffff00',
      arcane: '#cc66ff',
      holy: '#ffff99',
      shadow: '#9933ff',
    };

    let effectsHtml = '';
    for (const effect of skill.effects) {
      if (effect.type === 'damage') {
        const color = damageTypes[effect.damageType || 'physical'];
        effectsHtml += `<div style="color: ${color};">Deals ${effect.value} ${effect.damageType || 'physical'} damage</div>`;
      } else if (effect.type === 'heal') {
        effectsHtml += `<div style="color: #00ff00;">Heals for ${effect.value}</div>`;
      } else if (effect.type === 'buff') {
        effectsHtml += `<div style="color: #00ff00;">+${effect.value} ${effect.stat} for ${effect.duration}s</div>`;
      } else if (effect.type === 'debuff') {
        effectsHtml += `<div style="color: #ff6666;">${effect.stat} for ${effect.duration}s</div>`;
      } else if (effect.type === 'dot') {
        effectsHtml += `<div style="color: #ff6666;">${effect.value} ${effect.damageType} over ${effect.duration}s</div>`;
      } else if (effect.type === 'hot') {
        effectsHtml += `<div style="color: #00ff00;">${effect.value} healing every ${effect.tickRate}s for ${effect.duration}s</div>`;
      }
    }

    this.skillTooltip.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 6px;">
        <span style="font-size: 24px; margin-right: 8px;">${skill.icon}</span>
        <span style="color: #fff; font-size: 14px; font-weight: bold;">${skill.name}</span>
      </div>
      <div style="color: #aaa; font-size: 11px; margin-bottom: 8px;">${skill.description}</div>
      <div style="font-size: 11px; margin-bottom: 6px;">${effectsHtml}</div>
      <div style="display: flex; justify-content: space-between; font-size: 10px; color: #888;">
        <span>${skill.manaCost > 0 ? `${skill.manaCost} Mana` : 'No cost'}</span>
        <span>${skill.castTime > 0 ? `${skill.castTime}s cast` : 'Instant'}</span>
        <span>${skill.cooldown > 0 ? `${skill.cooldown}s CD` : 'No CD'}</span>
      </div>
      <div style="font-size: 10px; color: #666; margin-top: 4px;">Range: ${skill.range} yards</div>
    `;

    this.skillTooltip.style.display = 'block';
  }

  private hideSkillTooltip(): void {
    if (this.skillTooltip) {
      this.skillTooltip.style.display = 'none';
    }
  }

  public setActionBar(skillIds: string[]): void {
    this.currentActionBar = [...skillIds];

    for (let i = 0; i < this.actionSlots.length; i++) {
      const slot = this.actionSlots[i];
      const skillId = skillIds[i];
      const skill = skillId ? SKILLS[skillId] : null;

      const iconEl = slot.querySelector('.skill-icon') as HTMLSpanElement;
      const manaCostEl = slot.querySelector('.mana-cost') as HTMLSpanElement;

      if (skill) {
        iconEl.textContent = skill.icon;
        manaCostEl.textContent = skill.manaCost > 0 ? skill.manaCost.toString() : '';
        slot.style.opacity = '1';
      } else {
        iconEl.textContent = '';
        manaCostEl.textContent = '';
        slot.style.opacity = '0.5';
      }
    }
  }

  public updateActionBarCooldowns(getCooldownPercent: (skillId: string) => number, globalCooldownPercent: number): void {
    for (let i = 0; i < this.actionSlots.length; i++) {
      const slot = this.actionSlots[i];
      const skillId = this.currentActionBar[i];

      const cooldownOverlay = slot.querySelector('.cooldown-overlay') as HTMLDivElement;
      const cooldownSweep = slot.querySelector('.cooldown-sweep') as SVGCircleElement;
      const cooldownBling = slot.querySelector('.cooldown-bling') as HTMLDivElement;
      const cooldownText = slot.querySelector('.cooldown-text') as HTMLSpanElement;

      if (!skillId) {
        cooldownOverlay.style.opacity = '0';
        cooldownText.style.display = 'none';
        continue;
      }

      const skill = SKILLS[skillId];
      const skillCdPercent = getCooldownPercent(skillId);
      const cdPercent = Math.max(skillCdPercent, globalCooldownPercent);
      const circumference = 144.51; // 2 * PI * 23

      if (cdPercent > 0) {
        cooldownOverlay.style.opacity = '1';
        // Radial sweep: dashoffset goes from 0 (full) to circumference (empty)
        const dashOffset = circumference * (1 - cdPercent);
        if (cooldownSweep) {
          cooldownSweep.style.strokeDashoffset = dashOffset.toString();
        }

        if (skillCdPercent > 0 && skill) {
          const remaining = skillCdPercent * skill.cooldown;
          cooldownText.textContent = remaining.toFixed(1);
          cooldownText.style.display = 'block';
        } else {
          cooldownText.style.display = 'none';
        }
      } else {
        // Cooldown just finished - show bling effect
        if (cooldownOverlay.style.opacity === '1' && cooldownBling) {
          cooldownBling.style.opacity = '1';
          cooldownBling.style.transition = 'opacity 0.3s ease-out';
          setTimeout(() => {
            cooldownBling.style.opacity = '0';
          }, 50);
        }
        cooldownOverlay.style.opacity = '0';
        cooldownText.style.display = 'none';
      }
    }
  }

  public showCastBar(skillName: string): void {
    this.castBar.style.display = 'block';
    const nameEl = document.getElementById('cast-bar-name');
    if (nameEl) nameEl.textContent = skillName;
  }

  public updateCastBar(progress: number, remainingTime: number): void {
    const fillEl = document.getElementById('cast-bar-fill');
    const timeEl = document.getElementById('cast-bar-time');
    if (fillEl) fillEl.style.width = `${progress * 100}%`;
    if (timeEl) timeEl.textContent = `${remainingTime.toFixed(1)}s`;
  }

  public hideCastBar(): void {
    this.castBar.style.display = 'none';
  }

  public updateBuffs(buffs: Buff[]): void {
    // PERF: Check if buff list changed (avoid full rebuild)
    const currentBuffIds = buffs.map(b => b.id).join(',');

    if (currentBuffIds !== this.lastBuffIds) {
      // Buffs changed - rebuild only what's needed
      this.lastBuffIds = currentBuffIds;

      // Remove elements for expired buffs
      const activeIds = new Set(buffs.map(b => b.id));
      for (const [id, el] of this.buffElements) {
        if (!activeIds.has(id)) {
          el.remove();
          this.buffElements.delete(id);
        }
      }

      // Add new buffs
      for (const buff of buffs) {
        if (!this.buffElements.has(buff.id)) {
          const buffEl = this.createBuffElement(buff);
          this.buffElements.set(buff.id, buffEl);
          this.buffContainer.appendChild(buffEl);
        }
      }
    }

    // Update duration bars (cheap operation)
    for (const buff of buffs) {
      const buffEl = this.buffElements.get(buff.id);
      if (buffEl) {
        const progressBar = buffEl.querySelector('.buff-progress') as HTMLElement;
        if (progressBar) {
          progressBar.style.width = `${(buff.remainingDuration / buff.totalDuration) * 100}%`;
        }
      }
    }
  }

  private createBuffElement(buff: Buff): HTMLDivElement {
    const buffEl = document.createElement('div');
    buffEl.dataset.buffId = buff.id;
    const borderColor = buff.effect.type === 'debuff' ? '#ff4444' : '#44ff44';
    buffEl.style.cssText = `
      width: 32px;
      height: 32px;
      background: rgba(0,0,0,0.7);
      border: 1px solid ${borderColor};
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      font-size: 18px;
    `;

    buffEl.innerHTML = `
      <span>${buff.icon}</span>
      <span style="
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 3px;
        background: rgba(0,0,0,0.5);
      ">
        <span class="buff-progress" style="
          display: block;
          height: 100%;
          width: 100%;
          background: ${borderColor};
        "></span>
      </span>
    `;

    return buffEl;
  }

  private createNotificationArea(): void {
    const area = document.createElement('div');
    area.id = 'notification-area';
    area.style.cssText = `
      position: absolute;
      top: 150px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
    `;
    this.container.appendChild(area);
  }

  public setHealth(health: number): void {
    this.health = Math.max(0, Math.min(health, this.maxHealth));
    this.updateHealthBar();
  }

  public setMaxHealth(maxHealth: number): void {
    this.maxHealth = maxHealth;
    this.updateHealthBar();
  }

  private updateHealthBar(): void {
    const percent = (this.health / this.maxHealth) * 100;
    this.healthBar.style.width = `${percent}%`;
    this.healthText.textContent = `${Math.floor(this.health)}/${this.maxHealth}`;

    // Color based on health
    if (percent > 50) {
      this.healthBar.style.background = 'linear-gradient(180deg, #44ff44, #22aa22)';
    } else if (percent > 25) {
      this.healthBar.style.background = 'linear-gradient(180deg, #ffff44, #aaaa22)';
    } else {
      this.healthBar.style.background = 'linear-gradient(180deg, #ff4444, #aa2222)';
    }
  }

  public setMana(mana: number): void {
    this.mana = Math.max(0, Math.min(mana, this.maxMana));
    this.updateManaBar();
  }

  private updateManaBar(): void {
    const percent = (this.mana / this.maxMana) * 100;
    this.manaBar.style.width = `${percent}%`;
    this.manaText.textContent = `${Math.floor(this.mana)}/${this.maxMana}`;
  }

  public setLevel(level: number): void {
    this.level = level;
    this.levelText.textContent = level.toString();
  }

  public setExperience(exp: number, expToLevel: number): void {
    this.experience = exp;
    this.experienceToLevel = expToLevel;
    const percent = (exp / expToLevel) * 100;
    this.expBar.style.width = `${percent}%`;
    const expText = document.getElementById('exp-text');
    if (expText) {
      expText.textContent = `${exp}/${expToLevel}`;
    }
  }

  public addExperience(amount: number): void {
    this.experience += amount;
    if (this.experience >= this.experienceToLevel) {
      this.levelUp();
    }
    this.setExperience(this.experience, this.experienceToLevel);
    this.showNotification(`+${amount} XP`, '#cc99ff');
  }

  private levelUp(): void {
    this.level++;
    this.experience -= this.experienceToLevel;
    this.experienceToLevel = Math.floor(this.experienceToLevel * 1.5);
    this.setLevel(this.level);
    this.showNotification(`Level Up! Now level ${this.level}`, '#ffd700');
  }

  public setGold(gold: number): void {
    this.gold = gold;
    this.goldText.textContent = `💰 ${gold}`;
  }

  public updateQuests(quests: ActiveQuest[]): void {
    const list = document.getElementById('quest-list');
    if (!list) return;

    if (quests.length === 0) {
      list.innerHTML = '<div style="color: #888; font-style: italic;">No active quests</div>';
      return;
    }

    list.innerHTML = quests
      .map((quest) => {
        const questDef = QUESTS[quest.questId];
        if (!questDef) return '';

        const objectivesHtml = quest.objectives
          .map((obj) => {
            const complete = obj.current >= obj.required;
            return `
              <div style="color: ${complete ? '#44ff44' : '#ccc'}; margin-left: 10px; font-size: 11px;">
                ${complete ? '✓' : '○'} ${obj.targetName}: ${obj.current}/${obj.required}
              </div>
            `;
          })
          .join('');

        return `
          <div style="margin-bottom: 10px;">
            <div style="color: ${quest.status === 'complete' ? '#44ff44' : '#ffd700'}; font-weight: bold;">
              ${questDef.name}
            </div>
            ${objectivesHtml}
          </div>
        `;
      })
      .join('');
  }

  public showTarget(name: string, level: number, health: number, maxHealth: number): void {
    this.targetFrame.style.display = 'block';
    const nameEl = document.getElementById('target-name');
    const levelEl = document.getElementById('target-level');
    const healthBar = document.getElementById('target-health-bar');

    if (nameEl) nameEl.textContent = name;
    if (levelEl) levelEl.textContent = level.toString();
    if (healthBar) healthBar.style.width = `${(health / maxHealth) * 100}%`;
  }

  public hideTarget(): void {
    this.targetFrame.style.display = 'none';
  }

  public updateTargetHealth(health: number, maxHealth: number): void {
    const healthBar = document.getElementById('target-health-bar');
    if (healthBar) {
      healthBar.style.width = `${(health / maxHealth) * 100}%`;
    }
  }

  public showNotification(message: string, color: string = '#fff'): void {
    const area = document.getElementById('notification-area');
    if (!area) return;

    const notification = document.createElement('div');
    notification.style.cssText = `
      color: ${color};
      font-size: 16px;
      font-weight: bold;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
      animation: notification-fade 2s ease-out forwards;
    `;
    notification.textContent = message;

    // Add animation keyframes if not already added
    if (!document.getElementById('notification-styles')) {
      const style = document.createElement('style');
      style.id = 'notification-styles';
      style.textContent = `
        @keyframes notification-fade {
          0% { opacity: 1; transform: translateY(0); }
          70% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-30px); }
        }
      `;
      document.head.appendChild(style);
    }

    area.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 2000);
  }

  public showPickupNotification(itemIcon: string, itemName: string, quantity: number): void {
    const message = quantity > 1 ? `${itemIcon} ${itemName} x${quantity}` : `${itemIcon} ${itemName}`;
    this.showNotification(message, '#1eff00');
  }

  public destroy(): void {
    this.container.remove();
  }
}
